// Browser analytics tracker, modeled on Umami's client. It builds Umami-shaped packets
// ({ type, payload }) and ships them to the LoopQA ingest endpoint (/api/analytics), which attaches
// server-side fields (client IP, resolved user id, parsed user-agent) before storing them.
//
// Two layers live here:
//   1. The low-level tracker — session/visit identity, trackPageview, trackEvent.
//   2. Auto-instrumentation — initAutoInstrumentation() wires document-level listeners so that
//      every button click and input change in the app emits an event without per-component code.
//
// Privacy: raw values of free-text inputs are never sent (only their length); password fields are
// fully redacted. Use data-analytics-ignore to opt an element out, or data-analytics-event="name"
// to give it an explicit event tag.
import { trackFullStoryEvent } from './fullstory'

const ENDPOINT = '/api/analytics'
const WEBSITE_ID = 'loopqa'

const SESSION_KEY = 'loopqa:analytics:session'
const VISIT_KEY = 'loopqa:analytics:visit'
// A visit (Umami "visit") rotates after this much inactivity within the same browser session.
const VISIT_TIMEOUT_MS = 30 * 60 * 1000

function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback for environments without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function safeLocal(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

// Stable per-browser id. Persisted in localStorage so the same device is recognized across visits.
function getSessionId(): string {
  const ls = safeLocal()
  if (!ls) return 'ephemeral'
  let id = ls.getItem(SESSION_KEY)
  if (!id) {
    id = uuid()
    ls.setItem(SESSION_KEY, id)
  }
  return id
}

// Per-visit id that rotates after VISIT_TIMEOUT_MS of inactivity. Stored alongside a last-activity
// timestamp so a returning-after-lunch user starts a new visit but the same session.
function getVisitId(): string {
  const ls = safeLocal()
  if (!ls) return getSessionId()
  const now = Date.now()
  let id: string | null = null
  try {
    const raw = ls.getItem(VISIT_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as { id: string; ts: number }
      if (parsed && typeof parsed.id === 'string' && now - parsed.ts < VISIT_TIMEOUT_MS) {
        id = parsed.id
      }
    }
  } catch {
    id = null
  }
  if (!id) id = uuid()
  ls.setItem(VISIT_KEY, JSON.stringify({ id, ts: now }))
  return id
}

interface PacketOverrides {
  name?: string
  tag?: string | null
  data?: Record<string, unknown> | null
  url?: string
  title?: string
  referrer?: string
  error?: boolean
}

function buildPayload(overrides: PacketOverrides) {
  const loc = window.location
  return {
    website: WEBSITE_ID,
    session: getSessionId(),
    visit: getVisitId(),
    hostname: loc.hostname,
    screen: typeof screen !== 'undefined' ? `${screen.width}x${screen.height}` : undefined,
    language: typeof navigator !== 'undefined' ? navigator.language : undefined,
    title: overrides.title ?? document.title,
    url: overrides.url ?? loc.pathname + loc.search,
    referrer: overrides.referrer ?? document.referrer ?? '',
    name: overrides.name,
    tag: overrides.tag ?? undefined,
    data: overrides.data ?? undefined,
    ts: Date.now(),
    error: overrides.error ?? undefined,
  }
}

const enabled = true

function send(payload: ReturnType<typeof buildPayload>) {
  if (!enabled) return
  const packet = JSON.stringify({ type: 'event', payload })
  try {
    // sendBeacon survives page unloads (the most fragile moment for click/navigation events) and
    // doesn't block the main thread. Fall back to keepalive fetch where unavailable.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([packet], { type: 'application/json' })
      const ok = navigator.sendBeacon(ENDPOINT, blob)
      if (ok) return
    }
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: packet,
      credentials: 'same-origin',
      keepalive: true,
    }).catch(() => {})
  } catch {
    // Analytics must never throw into the app.
  }
}

// Marketing attribution params we lift off a landing URL (e.g. links in the completion email) and
// attach to the pageview event so the admin analytics can attribute visits back to a campaign.
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const

// The completion email stamps utm_source=completion-email on every link
// (see netlify/functions/lib/completion-email.ts). A pageview that lands carrying this source is a
// click-through from that email rather than organic navigation, so we tag it distinctly. This keeps
// it out of the regular (untagged) pageview bucket in the admin analytics, and the UTM params on the
// URL still attribute it to the campaign.
const COMPLETION_EMAIL_UTM_SOURCE = 'completion-email'
export const COMPLETION_EMAIL_CLICK_TAG = 'completion-email-click'

// Pull any utm_* params out of a URL or bare query string. Returns null when none are present.
function extractUtm(input: string | undefined): Record<string, string> | null {
  if (!input || input.indexOf('utm_') === -1) return null
  const qIdx = input.indexOf('?')
  const search = qIdx >= 0 ? input.slice(qIdx + 1) : input
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  } catch {
    return null
  }
  const out: Record<string, string> = {}
  for (const k of UTM_KEYS) {
    const v = params.get(k)
    if (v) out[k] = v.slice(0, 200)
  }
  return Object.keys(out).length ? out : null
}

// A pageview (Umami event_type 1) — sent on initial load and every client-side route change.
export function trackPageview(url?: string, title?: string) {
  const utm = extractUtm(url ?? (typeof window !== 'undefined' ? window.location.search : ''))
  // Clicks through from the completion email arrive with its campaign UTM; tag those pageviews so
  // they're distinguishable from regular page views in the admin analytics.
  const tag = utm && utm.utm_source === COMPLETION_EMAIL_UTM_SOURCE ? COMPLETION_EMAIL_CLICK_TAG : null
  send(buildPayload({ url, title, data: utm, tag }))
}

// The two events auto-instrumentation emits for every click and input change in the app. FullStory
// captures interactions natively and caps custom events at 60 per user per page per minute, so these
// are kept out of the forwarding below; only named domain events go over.
const AUTO_INSTRUMENTED_EVENTS = new Set(['click', 'input-change'])

// A custom event (Umami event_type 2). `tag` is the element/feature identity; `data` is arbitrary
// event properties. Named events are forwarded to FullStory as well, so instrumenting a funnel once
// makes it searchable in both places rather than only in the admin analytics.
export function trackEvent(name: string, data?: Record<string, unknown> | null, tag?: string | null) {
  send(buildPayload({ name, data: data ?? null, tag: tag ?? null }))
  if (!enabled || AUTO_INSTRUMENTED_EVENTS.has(name)) return
  trackFullStoryEvent(name, { ...(data ?? {}), ...(tag ? { tag } : {}) })
}

// An error event (a custom event flagged is_error server-side). Used for client-side failures we want
// to see in the admin errors view — e.g. the chunk-load failure behind the "Replay unavailable"
// fallback. `tag` identifies the failure kind (e.g. "replay-unavailable").
export function trackError(name: string, data?: Record<string, unknown> | null, tag?: string | null) {
  send(buildPayload({ name, data: data ?? null, tag: tag ?? null, error: true }))
}

// ---------------------------------------------------------------------------
// Global uncaught-error tracking
// ---------------------------------------------------------------------------

let globalErrorsInstalled = false
// De-dupe identical failures (a broken loop can fire window.onerror hundreds of times) and cap the
// number of distinct errors we'll ever report from one page, so a runaway error can't flood the
// analytics endpoint / the admin errors view.
const reportedErrorKeys = new Set<string>()
const MAX_DISTINCT_ERRORS = 50

function shouldReport(key: string): boolean {
  if (reportedErrorKeys.has(key)) return false
  if (reportedErrorKeys.size >= MAX_DISTINCT_ERRORS) return false
  reportedErrorKeys.add(key)
  return true
}

// Install window-level handlers so uncaught exceptions and unhandled promise rejections anywhere in
// the app are reported to the admin errors view (via trackError → /api/analytics → is_error). React
// render errors don't reach window.onerror — they're caught by <ErrorBoundary>, which reports
// separately — so this covers everything outside the React render tree (event handlers, timers,
// async code, third-party scripts). Idempotent and never throws into the app.
export function initGlobalErrorTracking() {
  if (globalErrorsInstalled || typeof window === 'undefined') return
  globalErrorsInstalled = true

  window.addEventListener('error', event => {
    try {
      // Resource-load failures (img/script/link) surface as error events whose target isn't the
      // window; they're noisy and not app exceptions, so ignore them.
      if (event.target && event.target !== window) return
      const err = event.error
      const message = err instanceof Error ? err.message : String(event.message ?? err ?? 'unknown error')
      if (!shouldReport(`uncaught:${message}`)) return
      trackError(
        'uncaught-exception',
        {
          message,
          stack: err instanceof Error ? err.stack?.slice(0, 2000) ?? null : null,
          source: event.filename || null,
          line: event.lineno || null,
          col: event.colno || null,
        },
        'uncaught-exception',
      )
    } catch {
      // an error handler must never throw
    }
  })

  window.addEventListener('unhandledrejection', event => {
    try {
      const reason = (event as PromiseRejectionEvent).reason
      const message = reason instanceof Error ? reason.message : String(reason ?? 'unknown rejection')
      if (!shouldReport(`rejection:${message}`)) return
      trackError(
        'unhandled-rejection',
        {
          message,
          stack: reason instanceof Error ? reason.stack?.slice(0, 2000) ?? null : null,
        },
        'unhandled-rejection',
      )
    } catch {
      // never throw
    }
  })
}

// ---------------------------------------------------------------------------
// Auto-instrumentation
// ---------------------------------------------------------------------------

const SENSITIVE_INPUT_TYPES = new Set(['password'])
// Free-text inputs: we record the value's length, never its content.
const TEXTUAL_INPUT_TYPES = new Set(['text', 'search', 'email', 'tel', 'url', 'number', 'textarea', 'date', 'datetime-local', 'time', 'month', 'week'])

interface ElementDescriptor {
  tag: string
  identity: string
  fields: Record<string, unknown>
}

function elementText(el: Element): string {
  const aria = el.getAttribute('aria-label')
  if (aria) return aria.trim()
  const title = el.getAttribute('title')
  if (title) return title.trim()
  const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim()
  return text.slice(0, 80)
}

// The text of the <label>(s) tied to a form control — either via `for=`/`htmlFor` (el.labels) or a
// wrapping <label>. Lets unlabeled-by-id inputs (e.g. a toggle whose text lives in a sibling <span>)
// still get a meaningful identity instead of collapsing to the bare tag "input".
function associatedLabelText(el: Element): string | null {
  const labels = (el as Partial<HTMLInputElement>).labels
  if (labels && labels.length) {
    const t = Array.from(labels).map(l => l.textContent ?? '').join(' ').replace(/\s+/g, ' ').trim()
    if (t) return t.slice(0, 80)
  }
  const wrap = el.closest('label')
  if (wrap) {
    const t = (wrap.textContent ?? '').replace(/\s+/g, ' ').trim()
    if (t) return t.slice(0, 80)
  }
  return null
}

// Build the event identity (bucket) + descriptive fields for a clicked/changed element. The bucket
// is driven SOLELY by an explicit `data-analytics-event` so event names stay predictable and
// low-cardinality. Everything descriptive (id, data-testid, name, visible text, label, placeholder —
// plus href and input metadata added by the callers) is still collected into `fields`, the event
// `data` payload, for drill-down, but is NEVER used as the bucket: in this app many ids/test-ids
// embed dynamic values (e.g. `project-card-<id>`), so using them as the name would scatter buckets.
// Elements without an explicit name fall into a stable generic `untagged-<tag>` bucket, surfacing
// coverage gaps instead of fragmenting across free text.
function describeElement(el: Element): ElementDescriptor {
  const tag = el.tagName.toLowerCase()
  const explicit = el.getAttribute('data-analytics-event')
  const id = el.getAttribute('id')
  const name = el.getAttribute('name')
  const testId = el.getAttribute('data-testid')
  const text = elementText(el)
  const role = el.getAttribute('role')
  const labelText = associatedLabelText(el)
  const placeholder = el.getAttribute('placeholder')

  const fields: Record<string, unknown> = {}
  if (id) fields.id = id
  if (name) fields.name = name
  if (testId) fields.testId = testId
  if (text) fields.text = text
  if (role) fields.role = role
  if (labelText) fields.label = labelText
  if (placeholder) fields.placeholder = placeholder
  fields.tag = tag

  const identity = explicit || `untagged-${tag}`
  return { tag, identity: identity.slice(0, 200), fields }
}

// Find the nearest meaningful interactive ancestor of an event target — so a click on the <span>
// inside a <button> still attributes to the button.
function closestInteractive(target: EventTarget | null): Element | null {
  let el = target instanceof Element ? target : null
  while (el) {
    if (el.hasAttribute('data-analytics-ignore')) return null
    const tag = el.tagName.toLowerCase()
    const role = el.getAttribute('role')
    if (tag === 'button' || tag === 'a' || role === 'button' || role === 'link' || role === 'tab' || role === 'menuitem' || el.hasAttribute('data-analytics-event')) {
      return el
    }
    el = el.parentElement
  }
  return null
}

function handleClick(ev: Event) {
  try {
    const el = closestInteractive(ev.target)
    if (!el) return
    const { identity, fields } = describeElement(el)
    if (el.tagName.toLowerCase() === 'a') {
      const href = el.getAttribute('href')
      if (href) fields.href = href
    }
    trackEvent('click', fields, identity)
  } catch {
    // never throw into app event handlers
  }
}

function describeInputValue(el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): Record<string, unknown> {
  const type = (el.getAttribute('type') ?? el.tagName.toLowerCase()).toLowerCase()
  const out: Record<string, unknown> = { inputType: type }

  if (el.tagName.toLowerCase() === 'select') {
    out.value = (el as HTMLSelectElement).value.slice(0, 120)
    return out
  }
  if (type === 'checkbox' || type === 'radio') {
    out.checked = (el as HTMLInputElement).checked
    const v = (el as HTMLInputElement).value
    if (v) out.value = v.slice(0, 120)
    return out
  }
  if (SENSITIVE_INPUT_TYPES.has(type)) {
    out.redacted = true
    return out
  }
  if (TEXTUAL_INPUT_TYPES.has(type)) {
    // Record only the length of free text — never its content.
    out.valueLength = (el as HTMLInputElement).value.length
    return out
  }
  // Other input kinds (range, color, etc.) — value is non-sensitive.
  out.value = String((el as HTMLInputElement).value ?? '').slice(0, 120)
  return out
}

function handleChange(ev: Event) {
  try {
    const target = ev.target
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) {
      return
    }
    if (target.closest('[data-analytics-ignore]')) return
    const { identity, fields } = describeElement(target)
    Object.assign(fields, describeInputValue(target))
    trackEvent('input-change', fields, identity)
  } catch {
    // never throw
  }
}

let instrumented = false

// Attach document-level listeners that instrument every button and input in the app. Idempotent —
// safe to call more than once (e.g. under React StrictMode double-invocation).
export function initAutoInstrumentation() {
  if (instrumented || typeof document === 'undefined') return
  instrumented = true
  // Capture phase so we still see the event even if a handler calls stopPropagation.
  document.addEventListener('click', handleClick, true)
  document.addEventListener('change', handleChange, true)
}
